import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FieldLabel } from '@shared/fields';
import { FONT_FAMILY_MAIN, NOTES_COLOR } from '../../theme';
import media from '../../utils/mediaQueries';
import { TextareaCounter } from '../TextareaCounter';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 3px;
`;

const Textarea = styled.textarea`
  padding: 13px 16px;
  border-radius: 2px;
  border: solid 1px rgba(0, 0, 0, 0.07);
  background-color: ${(props) => props.backgroundColor};
  outline: none;
  font-size: 14px;
  line-height: 1.43;
  letter-spacing: 0.2px;
  color: ${(props) => props.inputTextColor};
  transition: all 0.2s;
  resize: ${(props) => props.resize};
  font-family: ${FONT_FAMILY_MAIN};

  &::placeholder {
    color: #ccd0d4;
  }
  &:focus {
    border-color: #23c4f8 !important;
  }
  ${(props) => props.disabled
    ? `
    border: dashed 1px rgba(0, 0, 0, 0.07);
    color: #9aa1a9;
    &:hover {
      cursor: not-allowed;
    }
  `
    : `
    &:hover {
      border-color: rgba(41, 83, 102, 0.3);
    }
  `}
  ${(props) => props.valid === true
    && `
    border-color: #51e3ae !important;
  `}
  ${(props) => props.valid === false
    && `
    border-color: #f74656 !important;
    color: #f74656;
  `}
  width: ${(props) => (props.width ? props.width : 'auto')};

  ${media.mobile`
    font-size: 16px;
    
    &:focus {
      font-size: 16px;
    }
  `}
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  height: 20px;
  color: #f74656;
`;

const Helpers = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: -0.1px;
  color: ${(props) => props?.theme?.colors?.notesColor || NOTES_COLOR};

  ${media.mobile`
    flex-direction: column;
  `}
`;

const Textareafield = ({
  label,
  rows,
  cols,
  placeholder,
  disabled,
  valid,
  optionalLink,
  optionalText,
  width,
  tabIndex,
  name,
  onChange,
  onBlur,
  value,
  errorMessage,
  minLength,
  supplementaryInfo,
  textareaRef,
  labelTextColor,
  inputTextColor,
  backgroundColor,
  resize,
}) => (
  <InputContainer>
    <FieldLabel
      label={label}
      optionalLink={optionalLink}
      optionalText={optionalText}
      valid={valid}
      labelTextColor={labelTextColor}
    />
    {(minLength || supplementaryInfo) && (
      <Helpers>
        {minLength && (
          <div>
            <TextareaCounter minLength={minLength} value={value} />
          </div>
        )}
        {supplementaryInfo && <div>{supplementaryInfo}</div>}
      </Helpers>
    )}
    <Textarea
      rows={rows}
      cols={cols}
      tabIndex={tabIndex}
      placeholder={placeholder}
      disabled={disabled}
      valid={valid}
      width={width}
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      data-test-selector={`form-field-${name}`}
      ref={textareaRef}
      backgroundColor={backgroundColor}
      inputTextColor={inputTextColor}
      resize={resize}
    />
    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
  </InputContainer>
);

Textareafield.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  rows: PropTypes.string,
  cols: PropTypes.string,
  optionalLink: PropTypes.string,
  optionalText: PropTypes.string,
  disabled: PropTypes.bool,
  valid: PropTypes.bool,
  width: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.any,
  errorMessage: PropTypes.string,
  tabIndex: PropTypes.string,
  minLength: PropTypes.number,
  supplementaryInfo: PropTypes.node,
  textareaRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  labelTextColor: PropTypes.string,
  inputTextColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  resize: PropTypes.string,
};

Textareafield.defaultProps = {
  label: '',
  placeholder: '',
  rows: '5',
  cols: '',
  disabled: false,
  valid: null,
  width: '',
  optionalLink: '',
  optionalText: '',
  name: '',
  onChange: () => {},
  onBlur: () => {},
  value: '',
  errorMessage: '',
  tabIndex: null,
  minLength: null,
  supplementaryInfo: null,
  textareaRef: null,
  labelTextColor: '#354354',
  inputTextColor: '#354354',
  backgroundColor: '#f4f7f9',
  resize: 'none',
};

export default Textareafield;
